import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Bell, 
  Database, 
  Eye, 
  Zap, 
  Target, 
  Palette,
  Settings,
  Save,
  Trash2,
  RefreshCw,
  Info
} from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import ThemeSwitcher from '@/components/settings/ThemeSwitcher';

const SystemSettingsPage = () => {
  const {
    // Header and Navigation
    isStickyHeaderEnabled,
    toggleStickyHeader,
    
    // Notifications
    enableNotifications,
    toggleNotifications,
    enableSoundAlerts,
    toggleSoundAlerts,
    enableEmailNotifications,
    toggleEmailNotifications,
    
    // Data and Privacy
    autoSaveEnabled,
    toggleAutoSave,
    dataRetentionDays,
    setDataRetentionDays,
    enableAnalytics,
    toggleAnalytics,
    
    // UI and Display
    enableAnimations,
    toggleAnimations,
    compactMode,
    toggleCompactMode,
    showTooltips,
    toggleTooltips,
    enableHoverEffects,
    toggleHoverEffects,
    
    // Performance
    enableCaching,
    toggleCaching,
    cacheExpiryHours,
    setCacheExpiryHours,
    enableLazyLoading,
    toggleLazyLoading,
    
    // Cricket Specific
    defaultOvers,
    setDefaultOvers,
    enableBallByBallUpdates,
    toggleBallByBallUpdates,
    showDetailedStats,
    toggleDetailedStats,
    enableLiveScoreboard,
    toggleLiveScoreboard,
  } = useSettings();

  const handleResetSettings = () => {
    if (confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const handleClearCache = () => {
    if (confirm('Are you sure you want to clear all cached data? This will improve performance but may temporarily slow down the app.')) {
      // Clear all cached data
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('cache_') || key.includes('temp_')) {
          localStorage.removeItem(key);
        }
      });
      alert('Cache cleared successfully!');
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">System Settings</h1>
        </div>
        <p className="text-muted-foreground">
          Customize your cricket dashboard experience with comprehensive settings for appearance, 
          notifications, performance, and cricket-specific features.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={handleClearCache} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Clear Cache
        </Button>
        <Button variant="outline" onClick={handleResetSettings} className="flex items-center gap-2">
          <Trash2 className="h-4 w-4" />
          Reset to Defaults
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Theme and Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Theme & Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Theme</Label>
                <div className="mt-2">
                  <ThemeSwitcher />
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="animations">Enable Animations</Label>
                    <p className="text-xs text-muted-foreground">
                      Smooth transitions and animations throughout the app
                    </p>
                  </div>
                  <Switch
                    id="animations"
                    checked={enableAnimations}
                    onCheckedChange={toggleAnimations}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="compact-mode">Compact Mode</Label>
                    <p className="text-xs text-muted-foreground">
                      Reduce spacing for more content on screen
                    </p>
                  </div>
                  <Switch
                    id="compact-mode"
                    checked={compactMode}
                    onCheckedChange={toggleCompactMode}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="tooltips">Show Tooltips</Label>
                    <p className="text-xs text-muted-foreground">
                      Display helpful hints on hover
                    </p>
                  </div>
                  <Switch
                    id="tooltips"
                    checked={showTooltips}
                    onCheckedChange={toggleTooltips}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="hover-effects">Hover Effects</Label>
                    <p className="text-xs text-muted-foreground">
                      Interactive hover animations on elements
                    </p>
                  </div>
                  <Switch
                    id="hover-effects"
                    checked={enableHoverEffects}
                    onCheckedChange={toggleHoverEffects}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="notifications">Enable Notifications</Label>
                <p className="text-xs text-muted-foreground">
                  Receive alerts for important events
                </p>
              </div>
              <Switch
                id="notifications"
                checked={enableNotifications}
                onCheckedChange={toggleNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="sound-alerts">Sound Alerts</Label>
                <p className="text-xs text-muted-foreground">
                  Audio notifications for live updates
                </p>
              </div>
              <Switch
                id="sound-alerts"
                checked={enableSoundAlerts}
                onCheckedChange={toggleSoundAlerts}
                disabled={!enableNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-notifications">Email Notifications</Label>
                <p className="text-xs text-muted-foreground">
                  Receive updates via email
                </p>
              </div>
              <Switch
                id="email-notifications"
                checked={enableEmailNotifications}
                onCheckedChange={toggleEmailNotifications}
                disabled={!enableNotifications}
              />
            </div>
          </CardContent>
        </Card>

        {/* Data & Privacy */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Data & Privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-save">Auto Save</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically save changes as you work
                </p>
              </div>
              <Switch
                id="auto-save"
                checked={autoSaveEnabled}
                onCheckedChange={toggleAutoSave}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="data-retention">Data Retention</Label>
                <Badge variant="secondary">{dataRetentionDays} days</Badge>
              </div>
              <Slider
                id="data-retention"
                value={[dataRetentionDays]}
                onValueChange={(value) => setDataRetentionDays(value[0])}
                max={365}
                min={7}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                How long to keep your data before automatic cleanup
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="analytics">Usage Analytics</Label>
                <p className="text-xs text-muted-foreground">
                  Help improve the app with anonymous usage data
                </p>
              </div>
              <Switch
                id="analytics"
                checked={enableAnalytics}
                onCheckedChange={toggleAnalytics}
              />
            </div>
          </CardContent>
        </Card>

        {/* Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="caching">Enable Caching</Label>
                <p className="text-xs text-muted-foreground">
                  Store data locally for faster loading
                </p>
              </div>
              <Switch
                id="caching"
                checked={enableCaching}
                onCheckedChange={toggleCaching}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="cache-expiry">Cache Expiry</Label>
                <Badge variant="secondary">{cacheExpiryHours}h</Badge>
              </div>
              <Slider
                id="cache-expiry"
                value={[cacheExpiryHours]}
                onValueChange={(value) => setCacheExpiryHours(value[0])}
                max={168}
                min={1}
                step={1}
                className="w-full"
                disabled={!enableCaching}
              />
              <p className="text-xs text-muted-foreground">
                How long to keep cached data before refreshing
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="lazy-loading">Lazy Loading</Label>
                <p className="text-xs text-muted-foreground">
                  Load content only when needed
                </p>
              </div>
              <Switch
                id="lazy-loading"
                checked={enableLazyLoading}
                onCheckedChange={toggleLazyLoading}
              />
            </div>
          </CardContent>
        </Card>

        {/* Cricket Specific */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Cricket Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="default-overs">Default Overs</Label>
              <Select value={defaultOvers.toString()} onValueChange={(value) => setDefaultOvers(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Overs</SelectItem>
                  <SelectItem value="10">10 Overs</SelectItem>
                  <SelectItem value="15">15 Overs</SelectItem>
                  <SelectItem value="20">20 Overs</SelectItem>
                  <SelectItem value="25">25 Overs</SelectItem>
                  <SelectItem value="30">30 Overs</SelectItem>
                  <SelectItem value="50">50 Overs</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Default number of overs for new matches
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="ball-updates">Ball-by-Ball Updates</Label>
                <p className="text-xs text-muted-foreground">
                  Real-time updates for each ball
                </p>
              </div>
              <Switch
                id="ball-updates"
                checked={enableBallByBallUpdates}
                onCheckedChange={toggleBallByBallUpdates}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="detailed-stats">Detailed Statistics</Label>
                <p className="text-xs text-muted-foreground">
                  Show comprehensive player and team stats
                </p>
              </div>
              <Switch
                id="detailed-stats"
                checked={showDetailedStats}
                onCheckedChange={toggleDetailedStats}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="live-scoreboard">Live Scoreboard</Label>
                <p className="text-xs text-muted-foreground">
                  Real-time scoreboard updates
                </p>
              </div>
              <Switch
                id="live-scoreboard"
                checked={enableLiveScoreboard}
                onCheckedChange={toggleLiveScoreboard}
              />
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Navigation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="sticky-header">Sticky Header</Label>
                <p className="text-xs text-muted-foreground">
                  Keep navigation header visible while scrolling
                </p>
              </div>
              <Switch
                id="sticky-header"
                checked={isStickyHeaderEnabled}
                onCheckedChange={toggleStickyHeader}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="space-y-2">
              <h4 className="font-medium">About Settings</h4>
              <p className="text-sm text-muted-foreground">
                All settings are automatically saved to your browser's local storage. 
                Changes take effect immediately. You can reset all settings to default 
                values using the "Reset to Defaults" button above.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemSettingsPage; 